import { type QueryKey, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  type BankAccountCreateBankAccountData,
  type BankAccountCreateBankAccountError,
  type BankAccountList,
  type BankAccountListBankAccountsData,
  type BankAccountWithDetails,
  type Options,
  bankAccountCreateBankAccount,
  bankAccountListBankAccountsQueryKey,
} from '@column/api';

import { bankAccountDropdownListBaseQueryKey } from './bankAccountDropdownQueryKey';

const LIST_QUERY_ID = bankAccountListBankAccountsQueryKey()[0]._id;

const matchesListKey = (queryKey: QueryKey): boolean => {
  const head = queryKey[0] as { _id?: string } | undefined;
  return head?._id === LIST_QUERY_ID;
};

const filtersFromKey = (queryKey: QueryKey): NonNullable<BankAccountListBankAccountsData['query']> => {
  const head = queryKey[0] as { query?: NonNullable<BankAccountListBankAccountsData['query']> } | undefined;
  return head?.query ?? {};
};

const filtersAcceptAccount = (
  filters: NonNullable<BankAccountListBankAccountsData['query']>,
  account: BankAccountWithDetails
): boolean => {
  if (filters.starting_after) return false;
  if (filters.query || filters.description) return false;
  if (filters.type && filters.type !== account.type) return false;
  if (filters.is_overdraftable !== undefined && filters.is_overdraftable !== account.is_overdraftable) return false;

  if (filters['created.gte'] && account.created_at < filters['created.gte']) return false;
  if (filters['created.lt'] && account.created_at >= filters['created.lt']) return false;

  const availableBalance = account.balances?.available_amount ?? 0;
  if (filters['balance.gte'] !== undefined && availableBalance < filters['balance.gte']) return false;
  if (filters['balance.lte'] !== undefined && availableBalance > filters['balance.lte']) return false;

  return true;
};

export const useCreateBankAccount = () => {
  const queryClient = useQueryClient();

  return useMutation<
    BankAccountWithDetails,
    BankAccountCreateBankAccountError,
    Options<BankAccountCreateBankAccountData>
  >({
    mutationFn: async (variables) => {
      const { data } = await bankAccountCreateBankAccount({ ...variables, throwOnError: true });
      return data;
    },
    onSuccess: (account) => {
      const entries = queryClient.getQueriesData<BankAccountList>({
        predicate: ({ queryKey }) =>
          matchesListKey(queryKey) && filtersAcceptAccount(filtersFromKey(queryKey), account),
      });
      entries.forEach(([queryKey, data]) => {
        if (!data) return;
        if (data.bank_accounts.some((entry) => entry.id === account.id)) return;
        queryClient.setQueryData<BankAccountList>(queryKey, {
          ...data,
          bank_accounts: [account, ...data.bank_accounts],
        });
      });
      queryClient.invalidateQueries({ queryKey: bankAccountDropdownListBaseQueryKey });
    },
  });
};
